import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardData, DashboardFilters, fetchDashboardData } from '../../api/Dashboard';

type Ctx = { data: DashboardData | null; loading: boolean; error: string | null; refresh: () => void };
const DashboardCtx = createContext<Ctx>({ data: null, loading: true, error: null, refresh: () => {} });

export const DashboardDataProvider: React.FC<{ children: React.ReactNode; filters?: DashboardFilters }> = ({ children, filters }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0); // changes to force refresh

  const refresh = () => setNonce(n => n + 1);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const d = await fetchDashboardData(filters);
        if (mounted) setData(d);
        setError(null);
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [filters, nonce]);

  const value = useMemo(() => ({ data, loading, error, refresh }), [data, loading, error]);
  return <DashboardCtx.Provider value={value}>{children}</DashboardCtx.Provider>;
};

export function useDashboardData() {
  return useContext(DashboardCtx);
}
